import React, { useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import { useStore } from '../store';

// A lightweight gold glowing Info Pin for non-zone landmarks (Bridge, Media
// Outlets). Matches the ZoneMarkers pin aesthetic — no large floating 3D text.
// Respects the global "Hide Panels" toggle. If `title` is passed it shows a
// hover label; otherwise it is a pure gold marker (host handles its own hover).
export default function InfoPin({ position = [0, 0, 0], y = 6, title, subtitle, seed = 0 }) {
  const dot = useRef();
  const halo = useRef();
  const beam = useRef();
  const [hover, setHover] = useState(false);
  const panelsHidden = useStore((s) => s.panelsHidden);
  const interactive = !!title;

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const pulse = 0.5 + 0.5 * Math.sin(t * 2.2 + seed);
    if (dot.current) dot.current.position.y = y + Math.sin(t * 1.6 + seed) * 0.5;
    if (halo.current) {
      halo.current.rotation.z = t * 0.8;
      const s = (hover ? 1.3 : 1) * (1 + pulse * 0.12);
      halo.current.scale.setScalar(s);
      halo.current.material.opacity = 0.45 + pulse * 0.2;
    }
    if (beam.current) beam.current.material.opacity = 0.12 + pulse * 0.05;
  });

  if (panelsHidden) return null;

  const handlers = interactive
    ? {
        onPointerOver: (e) => { e.stopPropagation(); setHover(true); document.body.style.cursor = 'pointer'; },
        onPointerOut: () => { setHover(false); document.body.style.cursor = 'auto'; },
      }
    : {};

  return (
    <group position={position}>
      {/* thin locator beam */}
      <mesh ref={beam} position={[0, y + 9, 0]}>
        <cylinderGeometry args={[0.12, 0.12, 18, 6]} />
        <meshBasicMaterial color="#D4AF37" transparent opacity={0.14} depthWrite={false} />
      </mesh>
      <group {...handlers}>
        <mesh ref={dot} position={[0, y, 0]}>
          <sphereGeometry args={[hover ? 1.15 : 0.9, 16, 16]} />
          <meshBasicMaterial color="#ffe58a" toneMapped={false} />
        </mesh>
        <mesh ref={halo} position={[0, y, 0]}>
          <ringGeometry args={[1.35, 1.65, 32]} />
          <meshBasicMaterial color="#D4AF37" transparent opacity={0.5} side={THREE.DoubleSide} toneMapped={false} />
        </mesh>
        {interactive && (
          <mesh position={[0, y, 0]} visible={false}>
            <sphereGeometry args={[4, 8, 8]} />
            <meshBasicMaterial transparent opacity={0} />
          </mesh>
        )}
        {interactive && hover && (
          <Html position={[0, y + 3.4, 0]} center distanceFactor={55} zIndexRange={[40, 0]} style={{ pointerEvents: 'none' }}>
            <div style={{ whiteSpace: 'nowrap', padding: '5px 12px', borderRadius: 9999, background: 'rgba(6,9,15,0.88)', border: '1px solid rgba(212,175,55,0.5)', textAlign: 'center' }}>
              <div style={{ color: '#ffe58a', fontFamily: 'monospace', fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase' }}>{title}</div>
              {subtitle && <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: 11, marginTop: 2 }}>{subtitle}</div>}
            </div>
          </Html>
        )}
      </group>
    </group>
  );
}
